import { get as lodashGet } from 'lodash'
import ABTestAPI from '@/api/abTest'
import sentry from '@/services/sentry'
import store from '@/vuex/store'
import OnboardingFeatureBasedOptionsAPI from '@/api/onboardingFeatureBasedOptions'

const AI_ONBOARDING_FLAG = 'AI-ONBOARDING'

const services = {
  async getAbTest(code) {
    const res = await ABTestAPI.getTest(code)

    return lodashGet(res, 'body.variant', null)
  },
}

const _state = {
  featureBasedOptions: null,
  loading: {
    featureBasedOptions: false,
    copyTemplate: false,
  },
}

const _getters = {
  skippableOnboarding: (_, __, ___, rootGetters) => {
    return rootGetters.isPaymentPage
  },
  skippedScreening: (_, __, rootState) => {
    return lodashGet(rootState, 'currentUser.personalInformation.skipUserScreening', false)
  },
  canUseOptimizedOnboarding: (_, ___, __, rootGetters) => {
    return rootGetters['featureToggles/isFeatureEnabled']('release-optimized-onboarding')
  },
  hasDoneScreening: (_, __, rootState) => {
    return lodashGet(rootState, 'currentUser.personalInformation.useAhaSlidesFor', null)
    && lodashGet(rootState, 'currentUser.personalInformation.userProfession', null)
  },
  isLoading: (state) => {
    return state.loading.featureBasedOptions || state.loading.copyTemplate
  },
  loadingFeatureBasedOptions: (state) => {
    return state.loading.featureBasedOptions
  },
}

const actions = {
  async getOnBoardingDataAction({ dispatch }) {
    await Promise.all([
      dispatch('getAiOnboardingAbTestAction'),
    ])
  },
  async getAiOnboardingAbTestAction({ commit }) {
    try {
      const variant = await services.getAbTest(AI_ONBOARDING_FLAG)
      if (!variant) {
        // eslint-disable-next-line no-console
        console.log('MISSING AI ONBOARDING AB TEST, REMEMBER TO ADD ITS RECORD IN OUR DATABASE')
        return
      }
      commit('updateCurrentABTest', { [AI_ONBOARDING_FLAG]: variant }, { root: true })
    } catch (e) {
      sentry.captureException(e)
    }
  },
  async getFeatureBasedOptionsAction({ commit }, params) {
    commit('setLoading', { type: 'featureBasedOptions', loading: true })
    let features = null

    try {
      if (store.getters['featureToggles/canUseOnboardingRemoveSpinnerWheel']) {
        features = await OnboardingFeatureBasedOptionsAPI.getFeaturesV2(params)
      } else {
        features = await OnboardingFeatureBasedOptionsAPI.getFeatures(params)
      }

      commit('setFeatureBasedOptions', features)
    } catch (e) {
      sentry.captureException(e)
    } finally {
      commit('setLoading', { type: 'featureBasedOptions', loading: false })
    }
  },
  async copyFromTemplateAction({ commit, getters }, { presentationId, callback }) {
    commit('setLoading', { type: 'copyTemplate', loading: true })
    try {
      const createOnboardingPresentation = getters.canUseOptimizedOnboarding ?
        OnboardingFeatureBasedOptionsAPI.createOnboardingPresentationV2 :
        OnboardingFeatureBasedOptionsAPI.createOnboardingPresentation
      const copiedPresentation = await createOnboardingPresentation({
        presentationId,
      })
      if (typeof callback === 'function') {
        callback(copiedPresentation)
      }
    } catch (error) {
      sentry.captureException(error)
      throw error
    } finally {
      commit('setLoading', { type: 'copyTemplate', loading: false })
    }
  },
}

const mutations = {
  setFeatureBasedOptions(state, features) {
    state.featureBasedOptions = features
  },
  setLoading(state, { type, loading }) {
    state.loading[type] = loading
  },
}

const onboarding = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}

export default onboarding
